import { useAuth0 } from "@auth0/auth0-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import AgentDomain from "entities/domain/agents/agent-domain";
import AgentNotificationConfigDomain, {
  NotificationCustomConfig,
} from "entities/domain/agents/agent-notification-config-domain";
import MerchantAgentDomain from "entities/domain/merchant-agents/merchant-agent-domain";
import { currentAgentQueryKey } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useAppSelector } from "redux/hooks";
import AgentsService, {
  EditAgentPayload,
  UpdateNotificationPreferencesPayload,
} from "services/agents";
import { replaceById } from "./query-cache-helpers";

const patchMerchantAgentDomain = (
  prev: MerchantAgentDomain,
  payload: EditAgentPayload
): MerchantAgentDomain => {
  return new MerchantAgentDomain({
    id: prev.id,
    name: payload.name ?? prev.name,
    surname: payload.surname ?? prev.surname,
    email: payload.email ?? prev.email,
    phone: payload.phone ?? prev.phone,
    preferredContactChannel: prev.preferredContactChannel,
    notificationConfig: prev.notificationConfig,
    merchantId: prev.merchantId,
    userId: prev.userId,
    profilePic: payload.profile_picture_url ?? prev.profilePic,
    color: prev.color,
    role: prev.role,
    permissions: prev.permissions,
    active: payload.active ?? prev.active,
    rotationEnabled:
      typeof payload.rotation_enabled === "boolean"
        ? payload.rotation_enabled
        : prev.rotationEnabled,
    playerIds: payload.player_ids ?? prev.playerIds,
    stylePreferences: prev.stylePreferences,
    lastActiveAt: prev.lastActiveAt,
    mfaEnrolledAt: prev.mfaEnrolledAt,
  });
};

export const teammatesAgentsQueryKey = (
  merchantId: number | null | undefined
) => ["teammates", "agents", merchantId ?? null];

export function useMerchantAgents(
  merchantId?: number,
  staleTimeMs: number = 60_000
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const resolvedMerchantId = merchantId ?? merchant?.id;

  return useQuery<MerchantAgentDomain[], Error>({
    queryKey: teammatesAgentsQueryKey(resolvedMerchantId),
    enabled: Boolean(resolvedMerchantId),
    queryFn: async () => {
      if (!resolvedMerchantId) return [];
      return AgentsService.getAgents(auth0Context, resolvedMerchantId);
    },
    staleTime: staleTimeMs,
    // Keep previous agents data while switching merchants to prevent spinner
    placeholderData: keepPreviousData,
  });
}

export const teammatesAgentQueryKey = (
  merchantId: number | null | undefined,
  agentId: string | null | undefined
) => ["teammates", "agent", merchantId ?? null, agentId ?? null];

export function useMerchantAgent(
  agentId: string | undefined,
  merchantId?: number
) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const resolvedMerchantId = merchantId ?? merchant?.id;

  return useQuery<MerchantAgentDomain, Error>({
    queryKey: teammatesAgentQueryKey(resolvedMerchantId, agentId),
    enabled: Boolean(resolvedMerchantId && agentId),
    queryFn: async () => {
      if (!resolvedMerchantId || !agentId) {
        throw new Error("Missing merchantId/agentId");
      }
      return AgentsService.getAgentById(
        auth0Context,
        { agentId },
        resolvedMerchantId
      );
    },
    staleTime: 60_000,
  });
}

export function useUpdateMerchantAgent() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    MerchantAgentDomain | null,
    Error,
    EditAgentPayload,
    { prev?: MerchantAgentDomain[] }
  >({
    mutationFn: async (payload) => {
      if (!merchant?.id) return null;
      return AgentsService.updateAgent(auth0Context, payload, merchant.id);
    },
    onMutate: async (payload) => {
      const key = teammatesAgentsQueryKey(merchant?.id);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<MerchantAgentDomain[]>(key);
      if (prev) {
        queryClient.setQueryData<MerchantAgentDomain[]>(
          key,
          prev.map((a) =>
            a.id === payload.id ? patchMerchantAgentDomain(a, payload) : a
          )
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          teammatesAgentsQueryKey(merchant?.id),
          ctx.prev
        );
      }
    },
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData<MerchantAgentDomain[]>(
        teammatesAgentsQueryKey(merchant?.id),
        (old) => replaceById(old ?? [], updated)
      );
      queryClient.setQueryData(
        teammatesAgentQueryKey(merchant?.id, updated.id.toString()),
        updated
      );
    },
  });
}

export function useUpdateNotificationPreferences() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    { disabledChannels: string[]; customConfigs: NotificationCustomConfig[] },
    Error,
    UpdateNotificationPreferencesPayload
  >({
    mutationFn: async (payload) => {
      if (!merchant?.id) throw new Error("Missing merchant");
      return AgentsService.updateNotificationPreferences(
        auth0Context,
        payload,
        merchant.id
      );
    },
    onSuccess: (prefs, variables) => {
      const buildConfig = (
        old: AgentNotificationConfigDomain | null
      ): AgentNotificationConfigDomain =>
        new AgentNotificationConfigDomain(
          old?.handle ?? null,
          old?.contactChannel ?? "",
          old?.preference ?? "",
          old?.strategy ?? "",
          old?.timezone ?? "",
          prefs.disabledChannels,
          prefs.customConfigs
        );

      // Cache 1: current-agent (AgentDomain has positional constructor — use Object.assign)
      queryClient.setQueryData<AgentDomain>(currentAgentQueryKey(), (old) => {
        if (!old || old.id !== variables.agentId) return old;
        return Object.assign(Object.create(Object.getPrototypeOf(old)), old, {
          notificationConfig: buildConfig(old.notificationConfig),
        });
      });

      // Cache 2: single MerchantAgentDomain
      queryClient.setQueryData<MerchantAgentDomain>(
        teammatesAgentQueryKey(merchant?.id, String(variables.agentId)),
        (old) =>
          old
            ? new MerchantAgentDomain({
                ...old,
                notificationConfig: buildConfig(old.notificationConfig),
              })
            : old
      );

      // Cache 3: agents list
      queryClient.setQueryData<MerchantAgentDomain[]>(
        teammatesAgentsQueryKey(merchant?.id),
        (old) =>
          old?.map((a) =>
            a.id === variables.agentId
              ? new MerchantAgentDomain({
                  ...a,
                  notificationConfig: buildConfig(a.notificationConfig),
                })
              : a
          ) ?? old
      );
    },
  });
}
